import { useEffect, useState } from "react";
import { AlertTriangle, Save, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface DocumentContentEditorProps {
  document: DocumentWithMetadata;
  currentContent: string | null;
  isOptimized: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSave: (content: string, isOptimized: boolean) => Promise<void>;
  isUpdating: boolean;
}

export const DocumentContentEditor = ({
  document,
  currentContent,
  isOptimized,
  isOpen,
  onClose,
  onSave,
  isUpdating,
}: DocumentContentEditorProps) => {
  const [editedContent, setEditedContent] = useState("");

  useEffect(() => {
    if (isOpen && currentContent) {
      setEditedContent(currentContent);
    }
  }, [isOpen, currentContent]);

  const handleSave = async () => {
    try {
      await onSave(editedContent, isOptimized);
      onClose();
    } catch (error) {
      console.error("Failed to save content:", error);
    }
  };

  const handleCancel = () => {
    setEditedContent(currentContent || "");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[110]" onClick={handleCancel} />

      {/* Modal */}
      <div className="fixed inset-4 bg-white shadow-xl z-[110] flex flex-col overflow-hidden rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit Document Content</h2>
            <p className="text-sm text-gray-500 mt-1">
              Editing {isOptimized ? "optimized" : "original"} content for "{document.name}"
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isUpdating}
              className="text-gray-600 border-gray-200 hover:bg-gray-50"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isUpdating || editedContent === currentContent}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Save className="h-4 w-4 mr-2" />
              {isUpdating ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-hidden">
          <div className="h-full flex flex-col">
            {document.el_doc_id && (
              <Alert className="border-amber-200 bg-amber-50 text-amber-800 mb-4">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription>
                  <strong>Knowledge Base Update:</strong> This document is currently in your
                  knowledge base. Saving changes will automatically update the knowledge base
                  version by replacing the existing document with your edits.
                </AlertDescription>
              </Alert>
            )}
            <Textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="flex-1 font-mono text-sm resize-none"
              placeholder="Enter document content..."
            />
            <div className="flex items-center justify-between text-sm text-gray-500 mt-3">
              <span>Characters: {editedContent.length}</span>
              <span>{editedContent !== currentContent ? "Unsaved changes" : "No changes"}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
